/**
 * #panel-collapse document tab: the entity title row and its right pane.
 *
 * Trailing cluster contract (2026-10-31): surface actions, then the one
 * secondary-pane toggle (PanelRight), then the overflow. Separators only
 * between visible controls. The pane toggle replaces the Info button on
 * documents and the "Edit" text button on Basecamp todos; only its label
 * changes per surface. The index tab uses the same toggle for its preview.
 */
import { useState, type MouseEvent } from 'react'
import { Save } from 'lucide-react'
import { toast } from 'sonner'
import { DocumentInfoDock, type InfoDockMode } from '@/components/features/document-info-dock'
import { TitleRowTrailing } from '@/components/layout/shell'
import { AddressBar } from '@/components/ui/address-bar'
import { IconButton } from '@/components/ui/icon-button'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { testIds } from '@/lib/test-ids'

const DOC_TITLE = 'Late-todo shadow'

const DETAIL_FIELDS = [
  ['Type', 'Note'],
  ['Path', 'hop-studios/internal/late-todo-shadow.md'],
  ['Updated', 'Today, 3:42 PM'],
  ['Owner', 'Rowan'],
] as const

export function PanelCollapseDocumentToolbar({
  paneOpen,
  onTogglePane,
  onMore,
  disabledReason,
}: {
  paneOpen: boolean
  onTogglePane: () => void
  /** Same item source as the tab right-click for this document (ADR-0020). */
  onMore: (event: MouseEvent<HTMLElement>) => void
  disabledReason?: string
}) {
  return (
    <nav
      className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-background/50 px-4 py-2"
      aria-label="Document actions"
      data-testid={testIds.maaterUiGallery.panelCollapseDocToolbar}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <NavigationButtons canGoBack={false} canGoForward={false} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <AddressBar
            segments={[
              { label: 'Hop Studios', path: 'hop-studios' },
              { label: 'internal', path: 'hop-studios/internal' },
            ]}
          />
          <h1 className="truncate text-lg font-semibold text-foreground">{DOC_TITLE}</h1>
        </div>
      </div>
      <TitleRowTrailing
        actions={[
          <IconButton
            key="save"
            icon={<Save className="h-4 w-4" />}
            label="Save document"
            variant="ghost"
            size="sm"
            onClick={() => toast.message('Saved')}
          />,
        ]}
        pane={{
          open: paneOpen,
          onToggle: onTogglePane,
          paneLabel: 'details',
          disabledReason,
          testId: testIds.maaterUiGallery.panelCollapseDocPaneToggle,
        }}
        onMore={onMore}
      />
    </nav>
  )
}

export function PanelCollapseDocumentBody() {
  return (
    <div className="min-h-0 flex-1 space-y-2 overflow-hidden px-6 py-4 text-sm" style={{ color: 'var(--ink-soft)' }}>
      <p>
        Shadow list of late Hop todos. The details pane is the same right column the index
        preview uses, with the same floor and the same PanelRight toggle.
      </p>
      <p>Save sits left of the toggle. The overflow is always last.</p>
    </div>
  )
}

export function PanelCollapseDocumentDock({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<InfoDockMode>('details')
  return (
    <DocumentInfoDock mode={mode} onModeChange={setMode} onClose={onClose}>
      {mode === 'details' ? (
        <div className="divide-y divide-border text-xs">
          {DETAIL_FIELDS.map(([field, value]) => (
            <div key={field} className="flex items-center gap-3 px-4 py-2">
              <span className="w-14 shrink-0" style={{ color: 'var(--ink-faint)' }}>
                {field}
              </span>
              <span className="truncate" style={{ color: 'var(--ink)' }}>
                {value}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 py-3 text-xs" style={{ color: 'var(--ink-soft)' }}>
          Version history for {DOC_TITLE}.
        </p>
      )}
    </DocumentInfoDock>
  )
}
